import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import PageTitle from './components/PageTitle';
import {
  Container,
  Form,
  Row,
  Col,
  Button,
  Table,
  Card,
  Badge,
  Dropdown,
  Nav,
  Tab,
  Tabs,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Accordion,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { API_ENDPOINTS } from './config/api';
import { Modal } from 'react-bootstrap';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

/**
 * EvaluationDashboard Component
 * 
 * To navigate to specific tabs from other components, use one of these methods:
 * 
 * 1. Using URL parameters:
 *    navigate('/evaluations?tab=assigned')
 *    navigate('/evaluations?tab=performance')
 *    navigate('/evaluations?tab=feedback')
 *    navigate('/evaluations?tab=survey')
 *    navigate('/evaluations?tab=templates')
 * 
 * 2. Using navigation state:
 *    navigate('/evaluations', { state: { tab: 'assigned' } })
 *    navigate('/evaluations', { state: { tab: 'performance' } })
 *    navigate('/evaluations', { state: { tab: 'feedback' } })
 *    navigate('/evaluations', { state: { tab: 'survey' } })
 *    navigate('/evaluations', { state: { tab: 'templates' } })
 * 
 * Available tabs: 'templates', 'assigned', 'performance', 'feedback', 'survey'
 */

// Helper: academic year runs July (6) to June (5) of the following year
const getCurrentAcademicYearStart = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based; July = 6
  return month >= 6 ? year : year - 1;
};
const EvaluationDashboard = () => {
  const [evaluations, setEvaluations] = useState({
    performance: [],
    feedback: [],
    survey: [],
    questionnaire: [],
  });
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [surveyResults, setSurveyResults] = useState([]);
  const [activeTab, setActiveTab] = useState("templates");
  const [selectedResident, setSelectedResident] = useState(null);
  const [residents, setResidents] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [feedbackData, setFeedbackData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(() => getCurrentAcademicYearStart());
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [assignedEvaluations, setAssignedEvaluations] = useState([]);
  const [assignedFilter, setAssignedFilter] = useState("all"); // all, performance, feedback, survey, questionnaire
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [templateCurrentPage, setTemplateCurrentPage] = useState(1);
  const [templateItemsPerPage, setTemplateItemsPerPage] = useState(10);
  const [assignedSearch, setAssignedSearch] = useState("");
  const [assignedCurrentPage, setAssignedCurrentPage] = useState(1);
  const [assignedItemsPerPage, setAssignedItemsPerPage] = useState(10);
  const [surveySearch, setSurveySearch] = useState("");
  const [surveyCurrentPage, setSurveyCurrentPage] = useState(1);
  const [surveyItemsPerPage, setSurveyItemsPerPage] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Update localStorage when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("evaluationDashboardTab", tab);
    
    // Update URL parameters to reflect the current tab
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('tab', tab);
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  // Initialize tab from URL params, navigation state, or localStorage
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const stateFromLocation = location.state?.tab;
    const tabFromStorage = localStorage.getItem("evaluationDashboardTab");
    
    // Priority: URL param > navigation state > localStorage > default
    const initialTab = tabFromUrl || stateFromLocation || tabFromStorage || "templates";
    setActiveTab(initialTab);
  }, [searchParams, location.state]);

  useEffect(() => {
    fetchEvaluations();
    const fetchTemplates = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/evaluation-templates`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) {
          // Handle non-2xx responses
          console.error(`Failed to fetch templates: ${res.status}`);
          return;
        }

        const data = await res.json();
        
        // Fetch complete template details including questions for each template
        const enhancedTemplates = await Promise.all(
          data.map(async (template) => {
            try {
              const templateRes = await fetch(
                `${API_ENDPOINTS.DIRECTOR}/evaluation-templates/${template.id}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              
              if (templateRes.ok) {
                const templateDetails = await templateRes.json();
                return {
                  ...template,
                  ...templateDetails // Include questions and other details
                };
              }
            } catch (error) {
              console.error(`Error fetching template ${template.id} details:`, error);
            }
            return template; // Return original template if details fetch fails
          })
        );
        
        setTemplates(enhancedTemplates);
      } catch (error) {
        console.error("Error fetching templates:", error);
        // Optionally set an error state to display a message to the user
      }
    };

    const fetchSurveyResults = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/survey-results`,
          {
          headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json();
        setSurveyResults(data);
      } catch (error) {
        console.error("Error fetching survey results:", error);
      }
    };

    const fetchAssignedEvaluations = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/evaluations-by-type/all`,
          {
          headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        // Flatten the evaluations array from all templates
        const flattenedEvaluations = data.flatMap((template) =>
          template.evaluations.map((evaluation) => ({
            ...evaluation,
            // Use the evaluation's actual title (which includes Midterm/Term labels)
            title: evaluation.title || template.title,
            evaluationType: template.evaluationType,
            dueDate: evaluation.dueDate || template.endTime, // Use template end time as fallback
            templateId: template.id, // Add templateId to each evaluation
          }))
        );
        console.log("Flattened evaluations:", flattenedEvaluations);
        setAssignedEvaluations(flattenedEvaluations);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching assigned evaluations:", error);
        setAssignedEvaluations([]); // Set empty array on error
        setLoading(false);
      }
    };

    const fetchResidents = async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/users`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setResidents(data.filter((user) => user.role === "resident"));
        }
      } catch (error) {
        console.error("Error fetching residents:", error);
      }
    };

    fetchTemplates();
    fetchSurveyResults();
    fetchAssignedEvaluations();
    fetchResidents();
  }, []);

  useEffect(() => {
    if (selectedResident) {
      fetchPerformanceData();
      fetchFeedbackData();
    }
  }, [selectedResident, selectedYear]);

  const fetchPerformanceData = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DIRECTOR}/performance-evaluations/${selectedResident}?year=${selectedYear}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPerformanceData(data);
      }
    } catch (error) {
      console.error("Error fetching performance data:", error);
    }
  };

  const fetchFeedbackData = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DIRECTOR}/feedback-evaluations/${selectedResident}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setFeedbackData(data);
      }
    } catch (error) {
      console.error("Error fetching feedback data:", error);
    }
  };

  const fetchEvaluations = async () => {
    try {
      const token = localStorage.getItem("token");
      const types = ["performance", "feedback", "survey", "questionnaire"];

      const results = await Promise.all(
        types.map((type) =>
          fetch(
            `${API_ENDPOINTS.DIRECTOR}/evaluations-by-type/${type}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ).then((res) => res.json())
        )
      );

      setEvaluations({
        performance: results[0],
        feedback: results[1],
        survey: results[2],
        questionnaire: results[3],
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching evaluations:", error);
      setLoading(false);
    }
  };

  // Add state for showing the confirm modal and the action to confirm
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState('Are you sure?');

  // Update handleDeleteTemplate to use the modal
  const handleDeleteTemplate = async (id) => {
    setConfirmMessage('Are you sure you want to delete this evaluation?');
    setConfirmAction(() => async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/evaluation-templates/${id}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.ok) {
          alert("Deleted");
          setTemplates(templates.filter((t) => t.id !== id));
        } else {
          const errorData = await res.json();
          console.error("Failed to delete template:", errorData);
          alert(`Failed to delete: ${errorData.message || "An error occurred"}`);
        }
      } catch (error) {
        console.error("Error deleting template:", error);
        alert("Failed to delete. Please try again later.");
      }
    });
    setShowConfirmModal(true);
  };

  const getStatusBadge = (status) => {
    const getStatusColor = (status) => {
      const variants = {
        pending: "warning",
        completed: "success",
        in_progress: "info",
      };
      return variants[status] || "secondary";
    };

    return (
      <div className="d-flex align-items-center justify-content-center">
        <span className="rounded px-2 py-1 bg-light">
          <i className={`bi bi-circle-fill align-middle me-2 text-${getStatusColor(status)}`} style={{fontSize: '0.4rem'}}></i>
          {status}
        </span>
      </div>
    );
  };

  // Helper function to capitalize names (from DirectorCaseSummaryPage)
  const capitalizeName = (name) => {
    if (!name) return '';
    return name.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Group residents by PGY level for sidebar
  const residentsByPGY = residents.reduce((acc, resident) => {
    const pgyLevel = resident.year || 'Unknown';
    if (!acc[pgyLevel]) {
      acc[pgyLevel] = [];
    }
    acc[pgyLevel].push(resident);
    return acc;
  }, {});
  const sortedPGYLevels = Object.keys(residentsByPGY).sort((a, b) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return parseInt(a) - parseInt(b);
  });

  // Add state for active PGY accordion
  const [activePGYAccordion, setActivePGYAccordion] = useState(() => {
    if (selectedResident) {
      const resident = residents.find(r => r.id === selectedResident);
      return resident ? String(resident.year) : (sortedPGYLevels[0] || '');
    }
    return sortedPGYLevels[0] || '';
  });

  // Update activePGYAccordion when selectedResident changes
  useEffect(() => {
    if (selectedResident) {
      const resident = residents.find(r => r.id === selectedResident);
      if (resident) setActivePGYAccordion(String(resident.year));
    }
  }, [selectedResident, residents]);

  // Always keep one accordion open
  useEffect(() => {
    if (!activePGYAccordion && sortedPGYLevels.length > 0) {
      setActivePGYAccordion(String(sortedPGYLevels[0]));
    }
  }, [activePGYAccordion, sortedPGYLevels]);

  // Sidebar component for resident selection
  const ResidentSidebar = ({ selectedResident, setSelectedResident }) => (
    <Card className="shadow-sm border-0 mb-4">
      <Card.Header className="px-3 py-4 d-flex border-bottom justify-content-between align-items-center">
        <h6 className="mb-0 fw-semibold">Residents</h6>
      </Card.Header>
      <Card.Body className="p-0">
        <Accordion activeKey={activePGYAccordion} onSelect={key => {
          // If user tries to close all, re-open the first available
          if (!key && sortedPGYLevels.length > 0) {
            setActivePGYAccordion(String(sortedPGYLevels[0]));
          } else {
            setActivePGYAccordion(key);
          }
        }} alwaysOpen={false}>
          {sortedPGYLevels.map(pgyLevel => (
            <Accordion.Item eventKey={String(pgyLevel)} key={pgyLevel} className="border-0 custom-accordian-border">
              <Accordion.Header>
                <span className="fw-semibold">PGY-{pgyLevel}</span>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <div className="list-group border-0 rounded-0">
                  {residentsByPGY[pgyLevel].map(resident => (
                    <button
                      key={resident.id}
                      className={`list-group-item list-group-item-action  border-0 ${selectedResident === resident.id ? 'list-group-item active' : ''}`}
                      onClick={() => {
                        setSelectedResident(resident.id);
                        setActivePGYAccordion(String(resident.year));
                      }}
                      style={{ fontSize: '0.85rem', cursor: 'pointer', }}
                    >
                        <span className="fw-medium">{capitalizeName(resident.firstName + ' ' + resident.lastName)}</span>
                    </button>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Card.Body>
    </Card>
  );

  // Refactor renderPerformanceComparison to use sidebar
  const renderPerformanceComparison = () => {
    const years = Array.from(
      { length: 3 },
      (_, i) => new Date().getFullYear() - 1 + i
    );
    return (
      <Row className="mt-4">
        <Col lg={2} md={3} sm={12}>
          <ResidentSidebar selectedResident={selectedResident} setSelectedResident={setSelectedResident} />
        </Col>
        <Col lg={10} md={9} sm={12} style={{display: 'flex', flexDirection: 'column' }}>
          <Card className="shadow-sm border-0 flex-grow-1 d-flex flex-column mb-4">
            <Card.Header className="border-0 px-4 py-3 d-flex justify-content-between align-items-center">
              <div className="d-flex flex-column">
                <h6 className="mb-0 fw-semibold">Performance Evaluations</h6>
                <p className="text-muted small mb-0">showing {performanceData.length} evaluations</p>
              </div>
              <Form.Group className="d-flex flex-row align-items-center gap-3 mb-0">
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  size="sm"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const currentAcademicStart = getCurrentAcademicYearStart();
                    const year = currentAcademicStart - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}-{year + 1}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Card.Header>
            <Card.Body className="p-0 flex-grow-1 d-flex flex-column">
              {selectedResident ? (
                performanceData.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr className="custom-header-bg">
                        <th className="px-4">Resident</th>
                        <th>Evaluation Title</th>
                        <th>Recurrence</th>
                        <th>Total Evaluations</th>
                        <th>Completed</th>
                        <th>Pending</th>
                        <th>Last Evaluation</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performanceData.map((group) => {
                        const completedCount = group.evaluations.filter(e => e.hasResponse).length;
                        const pendingCount = group.evaluations.filter(e => !e.hasResponse).length;
                        const totalCount = group.evaluations.length;
                        return (
                          <tr key={group.templateId}>
                            <td className="px-4">{group.residentName}</td>
                            <td>{group.title}</td>
                            <td>{group.recurrence || "One-time"}</td>
                            <td><Badge bg="secondary">{totalCount}</Badge></td>
                            <td><Badge bg="success">{completedCount}</Badge></td>
                            <td><Badge bg="warning">{pendingCount}</Badge></td>
                            <td>{group.evaluations[0]?.createdAt ? new Date(group.evaluations[0].createdAt).toLocaleDateString() : "No evaluations yet"}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/performance-history/${selectedResident}/${group.templateId}`)}
                              >
                                Results
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <Row>
                    <Col className="py-5">
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <img src="/images/undraw_blank-canvas_a6x5.svg" alt="No performance data" className="img-fluid" style={{height: '200px'}} />
                        <h5 className="mt-3">No Performance Data</h5>
                        <p className="text-center text-muted">
                          No performance evaluations found for the selected resident and year.
                        </p>
                      </div>
                    </Col>
                  </Row>
                )
              ) : (
                <Row>
                  <Col className="py-5">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <img src="/images/undraw_information-tab_6nod.svg" alt="No data" className="img-fluid" style={{height: '200px'}} />
                      <h5 className="mt-3">Select a Resident and Year</h5>
                      <p className="text-center text-muted mt-3">
                        Please select a resident and year to view performance data
                      </p>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // Refactor renderFeedbackComparison to use sidebar
  const renderFeedbackComparison = () => {
    return (
      <Row className="mt-4">
        <Col lg={2} md={3} sm={12}>
          <ResidentSidebar selectedResident={selectedResident} setSelectedResident={setSelectedResident} />
        </Col>
        <Col lg={10} md={9} sm={12} style={{display: 'flex', flexDirection: 'column' }}>
          <Card className="shadow-sm border-0 flex-grow-1 d-flex flex-column mb-4">
            <Card.Header className="border-0 px-4 py-3">
              <div className="d-flex flex-column">
                <h6 className="mb-0 fw-semibold">Feedback Evaluations</h6>
                <p className="text-muted small mb-0">showing {feedbackData.length} evaluations</p>
              </div>
            </Card.Header>
            {selectedResident ? (
              feedbackData.length > 0 ? (
                <Card.Body className="p-0 flex-grow-1 d-flex flex-column">
                  <Table responsive hover>
                    <thead>
                      <tr className="custom-header-bg">
                        <th className="px-4">Date</th>
                        <th>Evaluator</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedbackData.map((feedback, index) => (
                        <tr key={index}>
                          <td className="px-4">{new Date(feedback.createdAt).toLocaleDateString()}</td>
                          <td>{feedback.evaluator.firstName} {feedback.evaluator.lastName}</td>
                          <td>{feedback.template.title}</td>
                          <td>
                            <Badge className="rounded-2" bg={feedback.status === "completed" ? 'success' : 'warning'}>
                              {feedback.status}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                const responseId = feedback.responses[0]?.id;
                                if (responseId) {
                                  localStorage.setItem(
                                    "selectedFeedback",
                                    JSON.stringify({
                                      id: feedback.id,
                                      template: {
                                        id: feedback.template.id,
                                        title: feedback.template.title,
                                        evaluationType: feedback.template.evaluationType,
                                        questions: feedback.template.questions.map((q) => ({
                                          id: q.id,
                                          questionText: q.questionText,
                                          questionType: q.questionType,
                                          options: q.options,
                                          columns: q.columns,
                                        })),
                                      },
                                      evaluator: feedback.evaluator,
                                      status: feedback.status,
                                      createdAt: feedback.createdAt,
                                      answers: feedback.responses[0].answers,
                                    })
                                  );
                                  navigate(`/evaluations/response/${responseId}`);
                                } else {
                                  alert("No response found for this feedback");
                                }
                              }}
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              ) : (
                <Card.Body className="p-0 flex-grow-1 d-flex flex-column">
                  <div className="text-center p-5">
                    <img src="/images/undraw_blank-canvas_a6x5.svg" alt="No feedback data" className="img-fluid" style={{height: '200px'}} />
                    <h5 className="mt-3">No Feedback Evaluations</h5>
                    <p className="text-muted">
                      No feedback evaluations found for the selected resident.
                    </p>
                  </div>
                </Card.Body>
              )
            ) : (
              <Card.Body className="p-0 flex-grow-1 d-flex flex-column">
                <div className="text-center p-5">
                  <img src="/images/undraw_information-tab_6nod.svg" alt="Select resident" className="img-fluid" style={{height: '200px'}} />
                  <h5 className="mt-3">Select a Resident</h5>
                  <p className="text-muted">
                    Please select a resident to view their feedback history.
                  </p>
                </div>
              </Card.Body>
            )}
          </Card>
        </Col>
      </Row>
    );
  };

  const renderSurveyAnalytics = () => {
    if (!evaluations.survey || !evaluations.questionnaire) return null;

    const allSurveyTemplates = templates.filter(
      (t) => t.type === "survey" || t.type === "questionnaire"
    );

    const normalizedSurveySearch = surveySearch.trim().toLowerCase();
    const filteredSurveyTemplates = normalizedSurveySearch
      ? allSurveyTemplates.filter((t) =>
          (t.title || "").toLowerCase().includes(normalizedSurveySearch)
        )
      : allSurveyTemplates;

    const totalSurveyTemplates = filteredSurveyTemplates.length;
    const surveyTotalPages = Math.ceil(
      totalSurveyTemplates / surveyItemsPerPage
    );
    const surveyStartIndex =
      totalSurveyTemplates === 0
        ? 0
        : (surveyCurrentPage - 1) * surveyItemsPerPage + 1;
    const surveyEndIndex = Math.min(
      surveyCurrentPage * surveyItemsPerPage,
      totalSurveyTemplates
    );
    const paginatedSurveyTemplates = filteredSurveyTemplates.slice(
      (surveyCurrentPage - 1) * surveyItemsPerPage,
      surveyCurrentPage * surveyItemsPerPage
    );

    console.log("All evaluations:", evaluations);
    console.log("Survey templates:", allSurveyTemplates);

    return (
      <Container className="mt-4">
        <Card className="shadow-sm border-0">
          <Card.Header className="border-0 px-4 py-3 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
            <div className="d-flex flex-column mb-2 mb-md-0">
              <h6 className="mb-0 fw-semibold">Survey Results</h6>
              <p className="text-muted small mb-0">
                Showing{" "}
                {totalSurveyTemplates === 0 ? 0 : surveyStartIndex} to{" "}
                {totalSurveyTemplates === 0 ? 0 : surveyEndIndex} of{" "}
                {totalSurveyTemplates} templates
              </p>
            </div>
            <div className="d-flex flex-row align-items-center justify-content-end gap-2">
              <Form.Control
                type="text"
                placeholder="Search surveys..."
                value={surveySearch}
                size="sm"
                onChange={(e) => {
                  setSurveySearch(e.target.value);
                  setSurveyCurrentPage(1);
                }}
              />
              <Form.Select
                value={surveyItemsPerPage}
                onChange={(e) => {
                  setSurveyItemsPerPage(parseInt(e.target.value, 10));
                  setSurveyCurrentPage(1);
                }}
                style={{ width: "auto" }}
                size="sm"
              >
                    <option value={5}>5/page</option>
                    <option value={10}>10/page</option>
                    <option value={25}>25/page</option>
                    <option value={50}>50/page</option>
              </Form.Select>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {totalSurveyTemplates > 0 ? (
              <>
                <Table responsive hover>
                  <thead>
                    <tr className="custom-header-bg">
                      <th className="ps-4 ">Title</th>
                      <th>Type</th>
                      <th>Questions</th>
                      <th>Responses</th>
                      <th>Pending</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSurveyTemplates.map((template) => {
                      // Get the survey results data for this template
                      const surveyResultData = surveyResults.find(
                        (result) => result.id === template.id
                      );

                      console.log(`Template ${template.id}:`, template);
                      console.log(
                        `Survey result data for ${template.id}:`,
                        surveyResultData
                      );

                      // Get assigned residents count
                      const assignedCount =
                        template.assignedResidents?.length || 0;

                      // Get completed responses count from survey results data
                      const completedCount =
                        surveyResultData?.completedResponses ||
                        surveyResultData?.totalResponses ||
                        0;

                      // Get questions count from template
                      const questionsCount =
                        template.questions?.length || 0;

                      // Get total responses received from survey results
                      const totalResponsesReceived =
                        surveyResultData?.totalResponses ||
                        surveyResultData?.responses?.length ||
                        completedCount; // Fallback to completed count

                      // Get submissions count (individual response submissions)
                      const submissionsCount =
                        surveyResultData?.responses?.length ||
                        surveyResultData?.totalResponses ||
                        totalResponsesReceived; // Fallback to total responses

                      // Debug: Log what we're finding for submissions
                      console.log(`Submissions data for ${template.id}:`, {
                        surveyResultsData: surveyResultData,
                        surveyResultsTotal: surveyResultData?.totalResponses,
                        surveyResultsResponses:
                          surveyResultData?.responses?.length,
                        fallback: totalResponsesReceived,
                        finalCount: submissionsCount,
                      });

                      // Calculate pending responses
                      const pendingCount = Math.max(
                        0,
                        assignedCount - totalResponsesReceived
                      );

                      console.log(`Counts for ${template.id}:`, {
                        assigned: assignedCount,
                        completed: completedCount,
                        totalResponses: totalResponsesReceived,
                        submissions: submissionsCount,
                        questions: questionsCount,
                        pending: pendingCount,
                        surveyResultData: surveyResultData,
                        template: template,
                      });

                      return (
                        <tr key={template.id}>
                          <td className="ps-4">{template.title}</td>
                          <td>
                            <Badge
                              bg={
                                template.type === "survey"
                                  ? "success"
                                  : "warning"
                              }
                            >
                              {template.type}
                            </Badge>
                          </td>
                          <td>{questionsCount}</td>
                          <td>
                            <div className="d-flex flex-column">
                              <div className="d-flex justify-content-between small">
                                <span>
                                  {totalResponsesReceived} / {assignedCount}
                                </span>
                                <span className="text-muted">
                                  {pendingCount} pending
                                </span>
                              </div>
                              <div
                                className="mt-1 rounded"
                                style={{
                                  height: 8,
                                  backgroundColor: "var(--gray-300)",
                                  minWidth: 80,
                                }}
                              >
                                <div
                                  className="rounded"
                                  style={{
                                    height: "100%",
                                    width: `${
                                      assignedCount > 0
                                        ? Math.min(
                                            100,
                                            Math.max(
                                              0,
                                              (totalResponsesReceived /
                                                assignedCount) *
                                                100
                                            )
                                          )
                                        : 0
                                    }%`,
                                    minWidth:
                                      assignedCount > 0 &&
                                      totalResponsesReceived > 0
                                        ? 4
                                        : 0,
                                    backgroundColor: "var(--blue)",
                                    transition: "width 0.2s ease",
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                          <td>{pendingCount}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const token =
                                    localStorage.getItem("token");
                                  const response = await fetch(
                                    `${API_ENDPOINTS.DIRECTOR}/survey-results/${template.id}`,
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    }
                                  );
                                  const data = await response.json();
                                  console.log("Response data:", data);

                                  const templateResponse = await fetch(
                                    `${API_ENDPOINTS.DIRECTOR}/evaluation-templates/${template.id}`,
                                    {
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    }
                                  );

                                  if (!templateResponse.ok) {
                                    throw new Error(
                                      "Failed to fetch template details"
                                    );
                                  }

                                  const templateDetails =
                                    await templateResponse.json();
                                  console.log(
                                    "Complete template data:",
                                    templateDetails
                                  );

                                  const templateData = {
                                    ...template,
                                    ...templateDetails,
                                    responses: data.responses || [],
                                    totalResponses: data.totalResponses || 0,
                                    totalAssigned: data.totalAssigned || 0,
                                    completedResponses:
                                      data.completedResponses || 0,
                                    averageScore: data.averageScore || 0,
                                  };
                                  localStorage.setItem(
                                    "selectedTemplate",
                                    JSON.stringify(templateData)
                                  );
                                  navigate(`/survey-results/${template.id}`);
                                } catch (error) {
                                  console.error(
                                    "Error fetching survey results:",
                                    error
                                  );
                                }
                              }}
                            >
                              View Results
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </>
            ) : normalizedSurveySearch ? (
              <div className="text-center p-5">
                <img
                  src="/images/undraw_blank-canvas_a6x5.svg"
                  alt="No matching surveys"
                  className="img-fluid"
                  style={{ height: "200px" }}
                />
                <h5 className="mt-3">No Matching Surveys</h5>
                <p className="text-muted mb-3">
                  Try adjusting or clearing your search terms.
                </p>
                {surveySearch && (
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => {
                      setSurveySearch("");
                      setSurveyCurrentPage(1);
                    }}
                  >
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center p-5">
                <img
                  src="/images/undraw_blank-canvas_a6x5.svg"
                  alt="No surveys"
                  className="img-fluid"
                  style={{ height: "200px" }}
                />
                <h5 className="mt-3">No Survey Templates</h5>
                <p className="text-muted">
                  No survey or questionnaire templates have been created yet.
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate("/evaluations/create")}
                  className="d-flex align-items-center gap-2 mx-auto"
                >
                  <i className="bi bi-plus-lg"></i> Create Survey Template
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
        <div className=" mt-3">
          <Pagination
            currentPage={surveyCurrentPage}
            totalPages={surveyTotalPages}
            onPageChange={setSurveyCurrentPage}
          />
        </div>
      </Container>
    );
  };

// Filter assigned evaluations based on selected filter
const filteredAssignedEvaluations = assignedEvaluations.filter(
  (evaluation) => {
    if (assignedFilter === "all") return true;
    return evaluation.evaluationType === assignedFilter;
  }
);

// Templates: search and pagination
const nonTemplateTemplates = templates.filter(
  (t) => t.evaluationType !== "template"
);
const templatesFilteredByType =
  templateFilter === "all"
    ? nonTemplateTemplates
    : nonTemplateTemplates.filter(
        (t) => (t.evaluationType || "").toLowerCase() === templateFilter
      );

const normalizedTemplateSearch = templateSearch.trim().toLowerCase();
const filteredTemplates = normalizedTemplateSearch
  ? templatesFilteredByType.filter((t) => {
      const title = (t.title || "").toLowerCase();
      const type = (t.evaluationType || "").toLowerCase();
      return (
        title.includes(normalizedTemplateSearch) ||
        type.includes(normalizedTemplateSearch)
      );
    })
  : templatesFilteredByType;

const totalTemplatesCount = filteredTemplates.length;
const templateTotalPages = Math.ceil(
  totalTemplatesCount / templateItemsPerPage
);
const templateStartIndex =
  totalTemplatesCount === 0
    ? 0
    : (templateCurrentPage - 1) * templateItemsPerPage + 1;
const templateEndIndex = Math.min(
  templateCurrentPage * templateItemsPerPage,
  totalTemplatesCount
);
const paginatedTemplates = filteredTemplates.slice(
  (templateCurrentPage - 1) * templateItemsPerPage,
  templateCurrentPage * templateItemsPerPage
);

// Update the groupRecurringEvaluations function to group by template AND due date
const groupRecurringEvaluations = (evaluations) => {
  console.log("Grouping evaluations:", evaluations);

  // Group by templateId AND due date - evaluations with same template and same due date are grouped together
  const groupedByTemplateAndDate = evaluations.reduce((acc, evaluation) => {
    if (evaluation.templateId && evaluation.dueDate) {
      // Create a key based on templateId and due date (normalized to date only)
      const dueDate = new Date(evaluation.dueDate).toDateString(); // This normalizes to date only
      const key = `template-${evaluation.templateId}-date-${dueDate}`;
      
      if (!acc[key]) {
        acc[key] = {
          id: key,
          title: evaluation.title,
          evaluationType: evaluation.evaluationType,
          templateId: evaluation.templateId,
          dueDate: evaluation.dueDate,
          evaluations: [],
          // Count unique residents
          uniqueResidents: new Set(),
        };
      }
      acc[key].evaluations.push(evaluation);
      if (evaluation.residentId) {
        acc[key].uniqueResidents.add(evaluation.residentId);
      }
    } else if (evaluation.templateId) {
      // For evaluations with templateId but no due date, group by template only
      const key = `template-${evaluation.templateId}-no-date`;
      if (!acc[key]) {
        acc[key] = {
          id: key,
          title: evaluation.title,
          evaluationType: evaluation.evaluationType,
          templateId: evaluation.templateId,
          dueDate: null,
          evaluations: [],
          uniqueResidents: new Set(),
        };
      }
      acc[key].evaluations.push(evaluation);
      if (evaluation.residentId) {
        acc[key].uniqueResidents.add(evaluation.residentId);
      }
    } else {
      // For evaluations without templateId, keep them as single entries
      acc[`single-${evaluation.id}`] = {
        id: evaluation.id,
        title: evaluation.title,
        evaluationType: evaluation.evaluationType,
        resident: evaluation.resident,
        dueDate: evaluation.dueDate,
        status: evaluation.status,
        responseId: evaluation.responseId,
        isSingle: true,
      };
    }
    return acc;
  }, {});

  // Convert to array and sort by due date, then by template title
  const result = Object.values(groupedByTemplateAndDate).map(group => ({
    ...group,
    evaluations: group.evaluations.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)),
    uniqueResidentsCount: group.uniqueResidents ? group.uniqueResidents.size : 0
  })).sort((a, b) => {
    // Sort by due date first, then by title
    if (a.dueDate && b.dueDate) {
      return new Date(a.dueDate) - new Date(b.dueDate);
    } else if (a.dueDate) {
      return -1;
    } else if (b.dueDate) {
      return 1;
    } else {
      return a.title.localeCompare(b.title);
    }
  });

  console.log("Grouped evaluations result:", result);
  return result;
};

// Assigned: search and pagination
const normalizedAssignedSearch = assignedSearch.trim().toLowerCase();
const filteredAssignedBySearch = normalizedAssignedSearch
  ? filteredAssignedEvaluations.filter((evaluation) => {
      const title = (evaluation.title || "").toLowerCase();
      const type = (evaluation.evaluationType || "").toLowerCase();
      const evaluatorName = evaluation.evaluations?.[0]?.evaluator
        ? `${evaluation.evaluations[0].evaluator.firstName || ""} ${
            evaluation.evaluations[0].evaluator.lastName || ""
          }`.toLowerCase()
        : "";
      return (
        title.includes(normalizedAssignedSearch) ||
        type.includes(normalizedAssignedSearch) ||
        evaluatorName.includes(normalizedAssignedSearch)
      );
    })
  : filteredAssignedEvaluations;

const groupedAssignedEvaluations =
  groupRecurringEvaluations(filteredAssignedBySearch);
const totalAssignedGroups = groupedAssignedEvaluations.length;
const assignedTotalPages = Math.ceil(
  totalAssignedGroups / assignedItemsPerPage
);
const assignedStartIndex =
  totalAssignedGroups === 0
    ? 0
    : (assignedCurrentPage - 1) * assignedItemsPerPage + 1;
const assignedEndIndex = Math.min(
  assignedCurrentPage * assignedItemsPerPage,
  totalAssignedGroups
);
const paginatedAssignedGroups = groupedAssignedEvaluations.slice(
  (assignedCurrentPage - 1) * assignedItemsPerPage,
  assignedCurrentPage * assignedItemsPerPage
);

const handleTemplateSelect = (templateId) => {
  setSelectedTemplates((prev) => {
    if (prev.includes(templateId)) {
      return prev.filter((id) => id !== templateId);
    } else {
      return [...prev, templateId];
    }
  });
};

// Update handleDeleteSelected to use the modal
const handleDeleteSelected = async () => {
  if (!selectedTemplates.length) return;
  setConfirmMessage(
    `Are you sure you want to delete ${selectedTemplates.length} selected template(s)?`
  );
  setConfirmAction(
    () =>
      async () => {
        try {
          const token = localStorage.getItem("token");
          await Promise.all(
            selectedTemplates.map((id) =>
              fetch(`${API_ENDPOINTS.DIRECTOR}/evaluation-templates/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
              })
            )
          );
          setTemplates(
            templates.filter((t) => !selectedTemplates.includes(t.id))
          );
          setSelectedTemplates([]);
          alert("Selected templates deleted successfully");
        } catch (error) {
          console.error("Error deleting templates:", error);
          alert("Failed to delete templates. Please try again later.");
        }
      }
  );
  setShowConfirmModal(true);
};

// Reusable pagination component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (!totalPages || totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="row mt-3">
      <div className="col-12 d-flex justify-content-end align-items-center">
        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
            </li>
            {getPageNumbers().map((page, index) => (
              <li
                key={index}
                className={`page-item ${
                  page === currentPage ? "active" : ""
                } ${page === "..." ? "disabled" : ""}`}
              >
                {page === "..." ? (
                  <span className="page-link">...</span>
                ) : (
                  <button
                    className="page-link"
                    onClick={() => onPageChange(page)}
                  >
                    {page}
                  </button>
                )}
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};



if (loading) {
  return (
    <div className="min-vh-100">
      <PageTitle title="Loading..." />
      <Navbar 
        pageTitle="Evaluation Dashboard" 
      />

      <div className="bg-white shadow-sm">
        <Container>
          <Row>
            <Col
              lg={2}
              md={6}
              sm={6}
              className="d-flex col-8 order-first order-lg-1 order-md-1 order-sm-1 mb-2 mb-md-2 mb-sm-2 py-lg-0 py-md-3 py-3 align-items-center justify-content-start"
            >
              <h6 className="mb-0 fw-semibold">Evaluation Manager</h6>
            </Col>
            <Col lg={7} md={12} sm={12} className="order-last order-lg-2 order-md-3 order-sm-3">
              <Nav 
                variant="tabs" 
                activeKey={activeTab} 
                onSelect={(k) => handleTabChange(k)}
                className="scrollable-tabs"
   
              >
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="templates"
                  >
                    Templates
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="assigned"
                  >
                    Assigned to Me
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="performance"
                  >
                    Performance
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="feedback"
                  >
                    Feedback
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="survey"
                  >
                    Surveys/Questionnaires
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
            <Col
              lg={3}
              md={6}
              sm={6}
              className="d-flex gap-2 col-4 order-lg-3 order-md-2 order-sm-2 mb-2 mb-md-2 mb-sm-2 align-items-center justify-content-end"
            >
              {activeTab === "templates" && (
                <div className="d-flex gap-2">
                  <Button 
                    variant="primary"
                    onClick={() => navigate("/evaluations/create")}
                    className="d-flex align-items-center gap-2"
                    size="sm"
                  >
                    <i className="bi bi-plus-lg"></i> <span className="d-none d-md-block">Create Evaluation</span>
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner 
            animation="border" 
            variant="primary" 
            size="lg"
            className="mb-3"
          />
          <div className="text-muted">Loading evaluation data...</div>
        </div>
      </Container>
    </div>
  );
}

return (
  <div className="min-vh-100">
    <PageTitle title="Evaluation Dashboard" description="Manage evaluations and templates for your residency program" />
    <Navbar 
      pageTitle="Evaluation Dashboard" 
    />

    <div className="bg-white shadow-sm custom-sticky-top">
      <Container>

        <Row>
          <Col
            lg={2}
            md={6}
            sm={6}
            className="d-flex col-8 order-first order-lg-1 order-md-1 order-sm-1 py-lg-0 py-md-3 py-3 align-items-center justify-content-start"
          >
            <h6 className="mb-0 fw-semibold">Evaluation Manager</h6>
          </Col>
            <Col lg={7} md={12} sm={12} className="order-last order-lg-2 order-md-3 order-sm-3">
              <Nav 
                variant="tabs" 
                activeKey={activeTab} 
                onSelect={(k) => handleTabChange(k)}
                className="scrollable-tabs"
                style={{marginBottom: '-1px'}}

              >
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="templates"
                  >
                    Templates
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="assigned"
                  >
                    Assigned to Me
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="performance"
                  >
                    Performance
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="feedback"
                  >
                    Feedback
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item style={{ flexShrink: 0 }}>
                  <Nav.Link 
                    eventKey="survey"
                  >
                    Surveys/Questionnaires
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
            <Col
            lg={3}
            md={6}
            sm={6}
            className="d-flex gap-2 col-4 order-lg-3 order-md-2 order-sm-2 py-lg-0 py-md-2 py-2 align-items-center justify-content-end"
          >
          {activeTab === "templates" && (
            <div className="d-flex gap-2">
            <Button 
              variant="primary"
              onClick={() => navigate("/evaluations/create")}
                className="d-flex align-items-center gap-2"
                size="sm"
            >
                <i className="bi bi-plus-lg"></i> <span className="d-none d-md-block">Create Evaluation</span>
            </Button>
            </div>
          )}
          </Col>
        </Row>
      </Container>
    </div>

    <Container className="mt-4 mb-3">
          {activeTab === "templates" && (
            <>
            <Row>
              <Col lg={2} className="mb-3">
                <Card className="border-0 bg-wight shadow-sm custom-sticky-top" style={{ top: "5rem" }}>
                  <Card.Header className="border-0 px-3 py-3">
                    <h6 className="mb-0 fw-semibold">Filter by Type</h6>
                  </Card.Header>
                  <Card.Body className="pe-0 ps-2 pt-0 pb-3">
                    <Nav className="flex-column" variant="pills">
                      <Nav.Link
                        active={templateFilter === "all"}
                        onClick={() => {
                          setTemplateFilter("all");
                          setTemplateCurrentPage(1);
                        }}
                      >
                        All Templates
                      </Nav.Link>
                      <Nav.Link
                        active={templateFilter === "performance"}
                        onClick={() => {
                          setTemplateFilter("performance");
                          setTemplateCurrentPage(1);
                        }}
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--blue)', borderRadius: '50%'}}></div> Performance</div>
                      </Nav.Link>
                      <Nav.Link
                        active={templateFilter === "feedback"}
                        onClick={() => {
                          setTemplateFilter("feedback");
                          setTemplateCurrentPage(1);
                        }}
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--indigo)', borderRadius: '50%'}}></div> Feedback</div>
                      </Nav.Link>
                      <Nav.Link
                        active={templateFilter === "survey"}
                        onClick={() => {
                          setTemplateFilter("survey");
                          setTemplateCurrentPage(1);
                        }}
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--green)', borderRadius: '50%'}}></div> Survey</div>
                      </Nav.Link>
                      <Nav.Link
                        active={templateFilter === "questionnaire"}
                        onClick={() => {
                          setTemplateFilter("questionnaire");
                          setTemplateCurrentPage(1);
                        }}
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--yellow)', borderRadius: '50%'}}></div> Questionnaire</div>
                      </Nav.Link>
                    </Nav>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={10}>
              <Card className="border-0 bg-wight shadow-sm">
                <Card.Header
                  className="border-0 px-4 py-3 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center"
                  style={{ backgroundColor: "var(--bs-body-bg)" }}
                >
                  <div className="d-flex flex-column mb-2 mb-md-0">
                    <h6 className="mb-0 fw-semibold">
                      Created Evaluation Templates
                    </h6>
                    <p className="text-muted small mb-0">
                      Showing{" "}
                      {totalTemplatesCount === 0 ? 0 : templateStartIndex} to{" "}
                      {totalTemplatesCount === 0 ? 0 : templateEndIndex} of{" "}
                      {totalTemplatesCount} templates
                    </p>
                  </div>
                  <div className="d-flex flex-row align-items-center justify-content-end gap-2">
                    {selectedTemplates.length > 0 && (
                      <Button
                        variant="danger"
                        onClick={handleDeleteSelected}
                        className="d-flex align-items-center gap-2 text-nowrap"
                        size="sm"
                      >
                        <i className="bi bi-trash"></i> Delete Selected (
                        {selectedTemplates.length})
                      </Button>
                    )}
                    <Form.Control
                      type="text"
                      placeholder="Search templates..."
                      value={templateSearch}
                      size="sm"
                      onChange={(e) => {
                        setTemplateSearch(e.target.value);
                        setTemplateCurrentPage(1);
                      }}
                    />
                    <Form.Select
                      value={templateItemsPerPage}
                      onChange={(e) => {
                        setTemplateItemsPerPage(parseInt(e.target.value, 10));
                        setTemplateCurrentPage(1);
                      }}
                      style={{ width: "auto" }}
                      size="sm"
                    >
                      <option value={5}>5/page</option>
                    <option value={10}>10/page</option>
                    <option value={25}>25/page</option>
                    <option value={50}>50/page</option>
                    </Form.Select>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
               
              {nonTemplateTemplates.length === 0 ? (
                <div className="text-center p-5">
                  <img src="/images/undraw_blank-canvas_a6x5.svg" alt="No templates" className="img-fluid" style={{height: '200px'}} />
                  <h5 className="mt-3">No Evaluation Templates</h5>
                  <p className="text-muted">
                    You haven't created any evaluation templates yet. Start by creating your first template.
                  </p>
                  <Button 
                    variant="primary"
                    onClick={() => navigate("/evaluations/create")}
                    className="d-flex align-items-center gap-2 mx-auto"
                  >
                    <i className="bi bi-plus-lg"></i> Create First Template
                  </Button>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr className="custom-header-bg">
                      <th className="px-4">Title</th>
                      <th>Type</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Assigned Residents</th>
                      <th>Status</th>
                      <th>Actions</th>
                        <th style={{ width: "40px" }}>
                          <Form.Check
                            type="checkbox"
                            checked={
                              nonTemplateTemplates.length > 0 &&
                              selectedTemplates.length ===
                                nonTemplateTemplates.length
                            }
                            onChange={() => {
                              if (
                                selectedTemplates.length ===
                                nonTemplateTemplates.length
                              ) {
                                setSelectedTemplates([]);
                              } else {
                                setSelectedTemplates(
                                  nonTemplateTemplates.map((t) => t.id)
                                );
                              }
                            }}
                          />
                        </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTemplates.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          <div className="text-muted mb-2">
                            No templates match your search.
                          </div>
                          {templateSearch && (
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => {
                                setTemplateSearch("");
                                setTemplateCurrentPage(1);
                              }}
                            >
                              Clear search
                            </Button>
                          )}
                        </td>
                      </tr>
                    ) : (
                      paginatedTemplates.map((t) => (
                      <tr key={t.id} style={{cursor: 'pointer'}} className={t.isDefault ? 'table-warning' : ''}>
                        <td className="px-4" onClick={() => navigate(`/evaluations/view/${t.id}`)}> 
                          {t.title}
                          {t.isDefault && (
                            <Badge bg="warning" className="ms-2">
                              Default
                            </Badge>
                          )}
                        </td>
                          <td onClick={() => navigate(`/evaluations/view/${t.id}`)}>
                            <Badge
                              bg={
                                t.evaluationType === "performance"
                                  ? "primary"
                                  : t.evaluationType === "feedback"
                                  ? "info"
                                  : t.evaluationType === "survey"
                                  ? "success"
                                  : "warning"
                              }
                            >
                              {t.evaluationType === "performance"
                                ? "Performance"
                                : t.evaluationType === "feedback"
                                ? "Feedback"
                                : t.evaluationType === "survey"
                                ? "Survey"
                                : "Questionnaire"}
                            </Badge>
                          </td>
                        <td onClick={() => navigate(`/evaluations/view/${t.id}`)}>
                          {new Date(t.startTime).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </td>
                        <td onClick={() => navigate(`/evaluations/view/${t.id}`)}>
                          {new Date(t.endTime).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </td>
                          <td onClick={() => navigate(`/evaluations/view/${t.id}`)}>
                            <i className="bi bi-people me-1 text-muted"></i>
                            {t.assignedResidents
                              ? t.assignedResidents.length
                              : 0}
                          </td>
                        <td onClick={() => navigate(`/evaluations/view/${t.id}`)}>
                          <Badge  className="rounded-2" bg={new Date(t.startTime) > new Date() ? 'secondary' : new Date(t.endTime) < new Date() ? 'danger' : 'success'}>
                              {new Date(t.startTime) > new Date()
                                ? "Draft"
                                : new Date(t.endTime) < new Date()
                                ? "Closed"
                                : "Open"}
                            </Badge>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                            <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light-lite"
                                  size="sm"
                                  id="dropdown-basic"
                                  className="no-arrow"
                                >
                                <i className="bi bi-three-dots-vertical"></i>
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() =>
                                      navigate(`/evaluations/edit/${t.id}`)
                                    }
                                    disabled={t.isDefault}
                                    title={t.isDefault ? "Default templates cannot be edited" : ""}
                                  >
                                  Edit
                                </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() =>
                                      navigate(
                                        `/evaluations/duplicate/${t.id}`
                                      )
                                    }
                                  >
                                  Duplicate
                                </Dropdown.Item>
                                <Dropdown.Item 
                                    onClick={() =>
                                      handleDeleteTemplate(t.id)
                                    }
                                  className="text-danger"
                                  disabled={t.isDefault}
                                  title={t.isDefault ? "Default templates cannot be deleted" : ""}
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                        </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              checked={selectedTemplates.includes(t.id)}
                              onChange={() => handleTemplateSelect(t.id)}
                            />
                          </td>
                      </tr>
                    ))
                    )}
                  </tbody>
                </Table>
              )}

              </Card.Body>
            </Card>
            </Col>
            <Col lg={12}>
           
            <div className=" mt-3">
                            <Pagination
                currentPage={templateCurrentPage}
                totalPages={templateTotalPages}
                onPageChange={setTemplateCurrentPage}
              />
            </div>
            
            </Col>
            </Row>
            </>
          )}
          {activeTab === "assigned" && (
          <>
            {/* Mobile filter menu */}
            <div className="d-md-none mb-3">
              <Accordion defaultActiveKey={null}>
                <Card className="border-0 bg-wight shadow-sm">
                  <Accordion.Item eventKey="0" className="border-0">
                    <Accordion.Header>Filter by Type</Accordion.Header>
                    <Accordion.Body className="p-0">
                      <Nav className="flex-column" variant="pills">
                        <Nav.Link
                          active={assignedFilter === "all"}
                          onClick={() => {
                            setAssignedFilter("all");
                            setAssignedCurrentPage(1);
                          }}
                          className="d-flex align-items-center gap-2 px-3 py-2"
                        >
                          <i className="bi bi-grid"></i> All Evaluations
                        </Nav.Link>
                        <Nav.Link
                          active={assignedFilter === "performance"}
                          onClick={() => {
                            setAssignedFilter("performance");
                            setAssignedCurrentPage(1);
                          }}
                          className="d-flex align-items-center gap-2 px-3 py-2"
                        >
                          <i className="bi bi-graph-up"></i> Performance
                        </Nav.Link>
                        <Nav.Link
                          active={assignedFilter === "feedback"}
                          onClick={() => {
                            setAssignedFilter("feedback");
                            setAssignedCurrentPage(1);
                          }}
                          className="d-flex align-items-center gap-2 px-3 py-2"
                        >
                          <i className="bi bi-chat-square-text"></i> Feedback
                        </Nav.Link>
                        <Nav.Link
                          active={assignedFilter === "survey"}
                          onClick={() => {
                            setAssignedFilter("survey");
                            setAssignedCurrentPage(1);
                          }}
                          className="d-flex align-items-center gap-2 px-3 py-2"
                        >
                          <i className="bi bi-ui-checks-grid"></i> Survey
                        </Nav.Link>
                        <Nav.Link
                          active={assignedFilter === "questionnaire"}
                          onClick={() => {
                            setAssignedFilter("questionnaire");
                            setAssignedCurrentPage(1);
                          }}
                          className="d-flex align-items-center gap-2 px-3 py-2"
                        >
                          <i className="bi bi-patch-question"></i> Questionnaire
                        </Nav.Link>
                      </Nav>
                    </Accordion.Body>
                  </Accordion.Item>
                </Card>
              </Accordion>
            </div>

            <Row className="mt-4">
              {/* Sidebar filter hidden on small screens */}
              <Col lg={2} md={3} className="d-none d-md-block">
                <Card className="border-0 bg-wight shadow-sm p-0 py-2 mb-3">
                  <Card.Header className="border-bottom-0 px-3" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                    <h6 className="mb-2 fw-semibold">Filter by Type</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Nav className="flex-column" variant="pills">
                      <Nav.Link
                        active={assignedFilter === "all"}
                        onClick={() => {
                          setAssignedFilter("all");
                          setAssignedCurrentPage(1);
                        }}
                        className="d-flex align-items-center gap-2"
                      >
                        All Evaluations
                      </Nav.Link>
                      <Nav.Link
                        active={assignedFilter === "performance"}
                        onClick={() => {
                          setAssignedFilter("performance");
                          setAssignedCurrentPage(1);
                        }}
                        className="d-flex align-items-center gap-2"
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--blue)', borderRadius: '50%'}}></div> Performance</div>
                      </Nav.Link>
                      <Nav.Link
                        active={assignedFilter === "feedback"}
                        onClick={() => {
                          setAssignedFilter("feedback");
                          setAssignedCurrentPage(1);
                        }}
                        className="d-flex align-items-center gap-2"
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--indigo)', borderRadius: '50%'}}></div> Feedback</div>
                      </Nav.Link>
                      <Nav.Link
                        active={assignedFilter === "survey"}
                        onClick={() => {
                          setAssignedFilter("survey");
                          setAssignedCurrentPage(1);
                        }}
                        className="d-flex align-items-center gap-2"
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--green)', borderRadius: '50%'}}></div> Survey</div>
                      </Nav.Link>
                      <Nav.Link
                        active={assignedFilter === "questionnaire"}
                        onClick={() => {
                          setAssignedFilter("questionnaire");
                          setAssignedCurrentPage(1);
                        }}
                        className="d-flex align-items-center gap-2"
                      >
                        <div className="d-flex align-items-center gap-2"><div style={{ width: '6px', height: '6px', backgroundColor: 'var(--yellow)', borderRadius: '50%'}}></div> Questionnaire</div>
                      </Nav.Link>
                    </Nav>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={10} md={9} sm={12}>
              <Card className="border-0 bg-wight shadow-sm rounded-3">
                <Card.Header
                  className="border-0 p-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center"
                  style={{ backgroundColor: "var(--bs-body-bg)" }}
                >
                  <div className="d-flex flex-column mb-2 mb-md-0">
                    <h6 className="mb-0 fw-semibold">Assigned Evaluations</h6>
                    <p className="text-muted small mb-0">
                      Showing{" "}
                      {totalAssignedGroups === 0 ? 0 : assignedStartIndex} to{" "}
                      {totalAssignedGroups === 0 ? 0 : assignedEndIndex} of{" "}
                      {totalAssignedGroups} evaluation groups
                    </p>
                  </div>
                  <div className="d-flex flex-row align-items-center justify-content-end gap-2">
                    <Form.Control
                      type="text"
                      placeholder="Search evaluations..."
                      value={assignedSearch}
                      size="sm"
                      onChange={(e) => {
                        setAssignedSearch(e.target.value);
                        setAssignedCurrentPage(1);
                      }}
                    />
                    <Form.Select
                      value={assignedItemsPerPage}
                      onChange={(e) => {
                        setAssignedItemsPerPage(
                          parseInt(e.target.value, 10)
                        );
                        setAssignedCurrentPage(1);
                      }}
                      style={{ width: "auto" }}
                      size="sm"
                    >
                      <option value={5}>5/page</option>
                    <option value={10}>10/page</option>
                    <option value={25}>25/page</option>
                    <option value={50}>50/page</option>
                    </Form.Select>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <Spinner animation="border" />
                  </div>
                ) : filteredAssignedEvaluations.length === 0 ? (
                  <div className="text-center p-5">
                    <img src="/images/undraw_blank-canvas_a6x5.svg" alt="No assigned evaluations" className="img-fluid" style={{height: '200px'}} />
                    <h5 className="mt-3">No Assigned Evaluations</h5>
                    <p className="text-muted">
                      You don't have any evaluations assigned to you at the moment.
                    </p>
                  </div>
                ) : filteredAssignedBySearch.length === 0 ? (
                  <div className="text-center p-5">
                    <img
                      src="/images/undraw_blank-canvas_a6x5.svg"
                      alt="No matching evaluations"
                      className="img-fluid"
                      style={{ height: "200px" }}
                    />
                    <h5 className="mt-3">No Matching Evaluations</h5>
                    <p className="text-muted mb-3">
                      Try adjusting or clearing your search terms.
                    </p>
                    {assignedSearch && (
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => {
                          setAssignedSearch("");
                          setAssignedCurrentPage(1);
                        }}
                      >
                        Clear search
                      </Button>
                    )}
                  </div>
                ) : (
                    <Table responsive hover className="align-middle">
                  <thead>
                    <tr className="custom-header-bg">
                          <th className="px-4">Title</th>
                          <th>Type</th>
                          <th>Due Date</th>
                          <th>Assigned Residents</th>
                          <th>Created By</th>
                          <th>Date Created</th>
                          <th>Evaluation Status</th>
                    </tr>
                  </thead>
                  <tbody>
                        {paginatedAssignedGroups.map((evaluation) => {
                          const completedCount = evaluation.evaluations.filter(e => e.status === "completed").length;
                          const pendingCount = evaluation.evaluations.filter(e => e.status !== "completed").length;
                          const totalCount = evaluation.evaluations.length;


                          return (
                            <tr key={evaluation.id} onClick={() => navigate(`/template-evaluations/${evaluation.templateId}?dueDate=${evaluation.dueDate ? new Date(evaluation.dueDate).toISOString() : ''}`)} style={{cursor: 'pointer'}}>
                              <td className="px-4"> {evaluation.title}
                                </td>
                              <td>
                                <Badge
                                  bg={
                                    evaluation.evaluationType === "performance"
                                      ? "primary"
                                      : evaluation.evaluationType === "feedback"
                                        ? "info"
                                        : evaluation.evaluationType === "survey"
                                          ? "success"
                                          : "warning"
                                  }
                                >
                                  {evaluation.evaluationType === "performance"
                                    ? "Performance"
                                    : evaluation.evaluationType === "feedback"
                                      ? "Feedback"
                                      : evaluation.evaluationType === "survey"
                                        ? "Survey"
                                        : "Questionnaire"}
                                </Badge>
                              </td>
                              <td>
                                {evaluation.dueDate ? (
                                  <span className="fw-medium">
                                    {new Date(evaluation.dueDate).toLocaleDateString(undefined, {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </span>
                                ) : (
                                  <span>No due date</span>
                                )}
                              </td>
                              <td>
                                <span className="fw-semibold"><i className="bi bi-people me-1"></i>{evaluation.uniqueResidentsCount}</span>
                              </td>
                              <td>
                                <div className="text-center">
                                  <span className="text-nowrap ">
                                    
                                    {evaluation.evaluations[0]?.evaluator?.firstName && evaluation.evaluations[0]?.evaluator?.lastName 
                                      ? `${evaluation.evaluations[0].evaluator.firstName} ${evaluation.evaluations[0].evaluator.lastName}`
                                      : "System"
                                    }
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="text-center">
                                  <span>
                                    {evaluation.evaluations[0]?.createdAt 
                                      ? new Date(evaluation.evaluations[0].createdAt).toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                      })
                                      : "Not set"
                                    }
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-row gap-1">
                                  <Badge bg="secondary">
                                    {totalCount} Total
                                  </Badge>
                                  {completedCount > 0 && (
                                  <Badge bg="success">
                                      <i className="bi bi-check-circle me-1"></i>{completedCount}
                                    </Badge>
                                  )}
                                  {pendingCount > 0 && (
                                    <Badge bg="warning">
                                      <i className="bi bi-clock me-1"></i>{pendingCount}
                                    </Badge>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
          <div className=" mt-3">
            <Pagination
              currentPage={assignedCurrentPage}
              totalPages={assignedTotalPages}
              onPageChange={setAssignedCurrentPage}
            />
          </div>
            </Col>
            </Row>
          </>
          )}
          {activeTab === "performance" && renderPerformanceComparison()}
          {activeTab === "feedback" && renderFeedbackComparison()}
          {activeTab === "survey" && renderSurveyAnalytics()}

    </Container>

    {/* Confirmation Modal */}
    <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-100">
          <div className="d-flex flex-column justify-content-start align-items-start">
            <span>Confirm Action</span>
            <div className="text-hl"></div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        {confirmMessage}
      </Modal.Body>
      <Modal.Footer className="px-4 border-top-0">
        <Button variant="light" onClick={() => setShowConfirmModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={async () => { setShowConfirmModal(false); await confirmAction(); }}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  </div>
);
};
export default EvaluationDashboard;
  
